import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Snackbar,
} from '@mui/material';
import {
  ClearAll as ClearIcon,
  Refresh as RefreshIcon,
  DeleteSweep as DeleteIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { divertilandiaColors } from '../theme/divertilandiaColors';

const Settings: React.FC = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleClearCache = async () => {
    try {
      // 1. Limpiar Service Worker Cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Cache del Service Worker limpiado');
      }

      // 2. Limpiar localStorage (excepto la autenticación)
      const authData = localStorage.getItem('divertilandia_auth');
      localStorage.clear();
      if (authData) {
        localStorage.setItem('divertilandia_auth', authData);
      }
      console.log('✅ LocalStorage limpiado (manteniendo sesión)');

      // 3. Limpiar sessionStorage
      sessionStorage.clear();
      console.log('✅ SessionStorage limpiado');

      // 4. Desregistrar y re-registrar Service Worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('✅ Service Workers desregistrados');
      }

      setOpenConfirmDialog(false);
      setSnackbarMessage('Caché limpiado exitosamente. La página se recargará en 2 segundos...');
      setShowSnackbar(true);

      // Recargar la página después de 2 segundos
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error limpiando caché:', error);
      setSnackbarMessage('Error al limpiar el caché. Intenta recargar la página manualmente.');
      setShowSnackbar(true);
      setOpenConfirmDialog(false);
    }
  };

  const handleHardRefresh = () => {
    // Hard refresh (Ctrl+Shift+R o Cmd+Shift+R)
    window.location.reload();
  };

  const getCacheInfo = async () => {
    let info = {
      caches: 0,
      cacheSize: 0,
      serviceWorkers: 0,
      localStorageItems: localStorage.length,
    };

    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        info.caches = cacheNames.length;
      }

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        info.serviceWorkers = registrations.length;
      }
    } catch (error) {
      console.error('Error obteniendo info de caché:', error);
    }

    return info;
  };

  const [cacheInfo, setCacheInfo] = useState<{
    caches: number;
    cacheSize: number;
    serviceWorkers: number;
    localStorageItems: number;
  }>({
    caches: 0,
    cacheSize: 0,
    serviceWorkers: 0,
    localStorageItems: 0,
  });

  React.useEffect(() => {
    getCacheInfo().then(setCacheInfo);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ fontSize: 40, color: divertilandiaColors.standard.primary, mr: 2 }} />
        <Typography variant="h4" sx={{ color: divertilandiaColors.standard.primary, fontWeight: 'bold' }}>
          Configuraciones
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        En esta sección puedes gestionar la configuración de la aplicación y resolver problemas comunes.
      </Alert>

      {/* Sección de Caché */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <ClearIcon sx={{ fontSize: 28, color: divertilandiaColors.rainbow.blue, mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Gestión de Caché
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            El caché almacena datos temporalmente para mejorar el rendimiento. Si experimentas 
            problemas como eventos que no aparecen o información desactualizada, limpia el caché.
          </Typography>

          <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Estado del Caché:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Service Worker Caches: ${cacheInfo.caches}`}
                  secondary="Cachés activos del Service Worker"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Service Workers: ${cacheInfo.serviceWorkers}`}
                  secondary="Workers registrados"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={`Items en LocalStorage: ${cacheInfo.localStorageItems}`}
                  secondary="Datos guardados localmente (tu sesión se mantendrá)"
                />
              </ListItem>
            </List>
          </Paper>

          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<ClearIcon />}
              onClick={() => setOpenConfirmDialog(true)}
              sx={{
                backgroundColor: divertilandiaColors.rainbow.orange,
                '&:hover': {
                  backgroundColor: divertilandiaColors.rainbow.red,
                },
              }}
            >
              Limpiar Todo el Caché
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleHardRefresh}
              sx={{
                borderColor: divertilandiaColors.standard.primary,
                color: divertilandiaColors.standard.primary,
              }}
            >
              Recargar Página
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Información Adicional */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <InfoIcon sx={{ fontSize: 28, color: divertilandiaColors.rainbow.violet, mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              ¿Cuándo limpiar el caché?
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Limpia el caché si experimentas alguno de estos problemas:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: divertilandiaColors.rainbow.green }} />
              </ListItemIcon>
              <ListItemText 
                primary="No ves todos los eventos en el calendario"
                secondary="Eventos que existen en la base de datos no aparecen"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: divertilandiaColors.rainbow.green }} />
              </ListItemIcon>
              <ListItemText 
                primary="La información no se actualiza"
                secondary="Cambios recientes no se reflejan en la aplicación"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: divertilandiaColors.rainbow.green }} />
              </ListItemIcon>
              <ListItemText 
                primary="Errores extraños o inesperados"
                secondary="La aplicación se comporta de manera inconsistente"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon sx={{ color: divertilandiaColors.rainbow.green }} />
              </ListItemIcon>
              <ListItemText 
                primary="Después de una actualización del sistema"
                secondary="Para asegurar que usas la versión más reciente"
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Nota:</strong> Limpiar el caché no cerrará tu sesión. Permanecerás autenticado.
          </Alert>
        </CardContent>
      </Card>

      {/* Dialog de confirmación */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle sx={{ 
          backgroundColor: divertilandiaColors.rainbow.orange, 
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          <ClearIcon sx={{ mr: 1 }} />
          Confirmar Limpieza de Caché
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" paragraph>
            ¿Estás seguro de que quieres limpiar todo el caché de la aplicación?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Esto eliminará:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Todos los cachés del Service Worker" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Datos temporales guardados localmente" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Service Workers registrados" />
            </ListItem>
          </List>
          <Alert severity="success" sx={{ mt: 2 }}>
            <strong>Tranquilo:</strong> Tu sesión se mantendrá activa. No tendrás que iniciar sesión de nuevo.
          </Alert>
          <Alert severity="info" sx={{ mt: 1 }}>
            La página se recargará automáticamente después de limpiar el caché.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleClearCache} 
            variant="contained"
            sx={{
              backgroundColor: divertilandiaColors.rainbow.orange,
              '&:hover': {
                backgroundColor: divertilandiaColors.rainbow.red,
              },
            }}
          >
            Sí, Limpiar Caché
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de confirmación */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;

